import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { NAV_LINKS, BRAND } from "@/config/site";
import { BookingButton } from "@/components/common/BookingButton";
import { cn } from "@/lib/utils";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  return (
    <header
      className={cn(
        "fixed inset-x-0 top-0 z-50 transition-all duration-500",
        scrolled
          ? "backdrop-blur-xl bg-legacy-charcoal/85 border-b border-white/10"
          : "bg-gradient-to-b from-legacy-charcoal/70 to-transparent"
      )}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex h-20 items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-3"
            data-testid="logo-link"
            aria-label="Legacy Spa — home"
          >
            <img
              src={BRAND.logo}
              alt="Legacy Spa logo"
              className="h-11 w-11 rounded-full bg-white object-contain p-1 ring-1 ring-white/10"
            />
            <span className="hidden sm:flex flex-col leading-none">
              <span className="font-heading text-xl text-white tracking-wide">
                Legacy Spa
              </span>
              <span className="text-[10px] uppercase tracking-[0.2em] text-legacy-mint/80">
                Wellness &amp; Massage
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) =>
                  cn(
                    "text-sm font-medium transition-colors hover:text-legacy-mint",
                    isActive ? "text-legacy-mint" : "text-white/80"
                  )
                }
                data-testid={`nav-${link.to.replace(/\//g, "") || "home"}`}
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:block">
            <BookingButton label="Book Now" size="sm" testId="header-book-btn" />
          </div>

          <button
            type="button"
            className="lg:hidden flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-white"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls="mobile-nav"
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            id="mobile-nav"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden absolute inset-x-0 top-20 border-t border-white/10 bg-legacy-charcoal/95 backdrop-blur-xl"
            data-testid="mobile-nav-panel"
          >
            <nav className="px-6 py-6 flex flex-col gap-1" aria-label="Mobile">
              {NAV_LINKS.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  className={({ isActive }) =>
                    cn(
                      "rounded-xl px-4 py-3 text-base font-medium transition-colors",
                      isActive
                        ? "bg-white/5 text-legacy-mint"
                        : "text-white/85 hover:bg-white/5"
                    )
                  }
                  data-testid={`mobile-nav-${link.to.replace(/\//g, "") || "home"}`}
                >
                  {link.label}
                </NavLink>
              ))}
              <div className="mt-4">
                <BookingButton
                  label="Book Now"
                  fullWidth
                  testId="mobile-header-book-btn"
                />
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
